import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { YesNoToggle } from '@app/components/core/toggle/YesNoToggle'
import { MultiSelect } from '@app/components/core/input/select/MultiSelect'
import { MultiLineTextInput } from '@app/components/core/input/text/MultiLineTextInput'
import { TagSelectionGroup } from '../blocks/TagSelectionGroup'
import { CreationStepLayout } from '../blocks/CreationStepLayout'
import { useQuery } from '@based/sdk/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInitiativeAutoSave } from '../../shared/useInitiativeAutoSave'
import { useTranslations } from '@i18n/useLanguage'
import { Style } from 'inlines'
import { SelectableTagModel } from '@app/types/TagModel'
import type { InitiativeWithId } from '../../shared/initiativeTypes'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '../../shared/workflow'
import { PhaseType } from '@schema/phase'
import type { OnboardingStep } from '@schema/initiative'
import { AmenityListModel } from 'functions/app/amenity/list'

const DEFAULT_WORKFLOW_STEP = initiativeCreationSteps.livingTogether

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

const yesNoToggleProps = {
  optionStyle: { flexGrow: 1 },
  containerStyle: { width: '100%' },
} as const

const inferSharingCommonSpaces = (initiative: InitiativeWithId): boolean => {
  if (initiative.sharingCommonSpaces !== undefined) {
    return initiative.sharingCommonSpaces
  }
  return (initiative.amenities?.length ?? 0) > 0
}

export const LivingTogetherStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
  workflowStep = DEFAULT_WORKFLOW_STEP,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
  workflowStep?: OnboardingStep
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const commonTranslations = useTranslations().app.common
  const emptyLabel = commonTranslations.notSpecified

  const amenitiesQuery = useQuery('list-amenities', {
    initiativeId: initiative.id,
  })
  const targetGroupsQuery = useQuery('list-target-groups')

  const [searchingForMembers, setSearchingForMembers] = useState(
    initiative.searchingForMembers ?? false,
  )
  const [searchingForMembersDescription, setSearchingForMembersDescription] =
    useState(initiative.searchingForMembersDescription ?? '')
  const [sharingCommonSpaces, setSharingCommonSpaces] = useState(() =>
    inferSharingCommonSpaces(initiative),
  )
  const [selectedAmenityIds, setSelectedAmenityIds] = useState<number[]>(() =>
    (initiative.amenities ?? []).map((amenity) => amenity.id),
  )
  const [selectedTargetGroups, setSelectedTargetGroups] = useState<number[]>(
    () => initiative.targetAudiences?.map((group) => group.id) ?? [],
  )

  const handleSharingCommonSpacesChange = useCallback((value: boolean) => {
    setSharingCommonSpaces(value)
    if (!value) {
      setSelectedAmenityIds([])
    }
  }, [])

  const amenityOptions = useMemo(
    () =>
      (amenitiesQuery.data ?? []).map((amenity: AmenityListModel) => ({
        value: amenity.id,
        label: amenity.name,
      })),
    [amenitiesQuery.data],
  )

  const toggleAmenity = useCallback((id: number) => {
    setSelectedAmenityIds((prev) => {
      const index = prev.indexOf(id)
      return index > -1 ? prev.toSpliced(index, 1) : [...prev, id]
    })
  }, [])

  const allTargetGroups: SelectableTagModel<number>[] = useMemo(
    () =>
      (targetGroupsQuery.data ?? []).map(
        (item: { id: number; name: string }) => ({
          key: item.id,
          value: item.name,
          selected: selectedTargetGroups.includes(item.id),
        }),
      ),
    [targetGroupsQuery.data, selectedTargetGroups],
  )

  const selectTargetGroup = useCallback((id: number) => {
    setSelectedTargetGroups((prev) => {
      const index = prev.indexOf(id)
      return index > -1 ? prev.toSpliced(index, 1) : [...prev, id]
    })
  }, [])

  const savePayload = useMemo(
    () => ({
      searchingForMembers,
      searchingForMembersDescription: searchingForMembers
        ? searchingForMembersDescription
        : '',
      sharingCommonSpaces,
      amenities: sharingCommonSpaces
        ? selectedAmenityIds.map((id) => ({ id }))
        : [],
      targetAudiences: selectedTargetGroups.map((id) => ({ id })),
      onboardingStep: workflowStep,
    }),
    [
      searchingForMembers,
      searchingForMembersDescription,
      sharingCommonSpaces,
      selectedAmenityIds,
      selectedTargetGroups,
      workflowStep,
    ],
  )

  useInitiativeAutoSave(initiative.id, savePayload)

  useEffect(() => {
    const memberDescriptionValid =
      !searchingForMembers || searchingForMembersDescription.trim().length > 0

    if (memberDescriptionValid) {
      completeStep(workflowStep)
      return
    }

    uncompleteStep(workflowStep)
  }, [
    searchingForMembers,
    searchingForMembersDescription,
    completeStep,
    uncompleteStep,
    workflowStep,
  ])

  const isSwappedStep = workflowStep !== DEFAULT_WORKFLOW_STEP

  return (
    <CreationStepLayout
      selectedPhaseType={selectedPhaseType}
      currentStep={workflowStep}
      titleOverride={
        isSwappedStep
          ? translations.creationStepTitles[DEFAULT_WORKFLOW_STEP]
          : undefined
      }
    >
      <Column gap="2rem" alignItems="stretch">
        <Column gap="0.5rem" alignItems="stretch">
          <Text.Medium style={labelStyle}>
            {translations.areYouLookingForMembers}
          </Text.Medium>
          <YesNoToggle
            yesSelected={searchingForMembers}
            setYesSelected={setSearchingForMembers}
            {...yesNoToggleProps}
          />
        </Column>

        {searchingForMembers && (
          <MultiLineTextInput
            label={translations.lookingForMembersTitle}
            description={translations.lookingForMembersSubtitle}
            labelStyle={labelStyle}
            required={true}
            value={searchingForMembersDescription}
            setValue={setSearchingForMembersDescription}
            placeholder={translations.lookingForMembersPlaceholder}
          />
        )}

        <Column gap="0.5rem" alignItems="stretch">
          <Text.Medium style={labelStyle}>
            {translations.livingTogetherShareCommonSpacesQuestion}
          </Text.Medium>
          <YesNoToggle
            yesSelected={sharingCommonSpaces}
            setYesSelected={handleSharingCommonSpacesChange}
            {...yesNoToggleProps}
          />
        </Column>

        {sharingCommonSpaces && (
          <MultiSelect<number>
            label={translations.livingTogetherSharedSpacesQuestion}
            labelStyle={labelStyle}
            subTitle={translations.selectOneOrMore}
            options={amenityOptions}
            selectedValues={selectedAmenityIds}
            onToggle={toggleAmenity}
            notSelectedLabel={emptyLabel}
          />
        )}

        <TagSelectionGroup
          label={translations.targetGroups}
          subTitle={translations.selectOneOrMore}
          collapsedAmountVisible={Math.max(selectedTargetGroups.length, 5)}
          tags={allTargetGroups}
          onSelect={selectTargetGroup}
        />
      </Column>
    </CreationStepLayout>
  )
}
